'use client'

/**
 * Companion gift shop — see promptchan-backend/myapp/gifting.py. Generally available
 * since the August 2026 A/B concluded; `gifts_enabled()` there is the kill switch.
 *
 * Self-gating on purpose: `/api/chat/gifts` returns `{enabled:false}` for the
 * control arm and for every account created before the experiment, and the hook
 * below leaves `enabled` false in that case. The parent can therefore mount this
 * unconditionally without knowing anything about the experiment, which keeps the
 * A/B wiring in one place instead of spreading arm checks through the chat UI.
 *
 * Affinity is deliberately NEVER shown or hinted at here — the server does not
 * even send it. The user sees a gift and her reaction, not a number going up.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { getIdToken } from 'firebase/auth'
import { FaGem } from 'react-icons/fa'

import Modal from './Modal'
import { purchaseIntent } from '../lib/gift-purchase'

const API = process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD

// react-confetti was already a dependency, so this adds no new package — but it
// is still ~15KB and pulls in a canvas, so it is code-split and only requested
// the first time someone actually sends a gift. ssr:false because it measures
// the window on mount.
// react-confetti is CJS and exports the forwardRef component as module.exports
// itself, with no `.default` and no __esModule flag. Bundler interop usually
// synthesises a `.default`, but `m.default || m` makes it work either way rather
// than depending on that — a wrong guess here would throw inside the chat.
const Confetti = dynamic(
  () => import('react-confetti').then((m) => m.default || m),
  { ssr: false },
)

const CONFETTI_MS = 2600

/**
 * Catalog + entitlement for the signed-in user.
 *
 * `enabled` stays false for the control arm and for any account created before
 * the experiment, which is what lets the chat mount the shop unconditionally.
 */
export function useGiftsEnabled(user) {
  const [state, setState] = useState({
    enabled: false,
    gifts: [],
    sections: [],
    gems: 0,
  })

  const refresh = useCallback(
    async (characterId) => {
      if (!user) return
      try {
        const idToken = await getIdToken(user)
        // character_id lets the server apply the CHARACTER's own openness gate,
        // so a modest companion never shows the After dark shelf. Optional:
        // omitting it only loses that gate — safe_mode is applied server-side
        // either way, and neither gate is ever the client's job to enforce.
        const qs = characterId
          ? `?character_id=${encodeURIComponent(characterId)}`
          : ''
        const res = await fetch(`${API}/api/chat/gifts${qs}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
        })
        if (!res.ok) return
        const data = await res.json()
        setState({
          enabled: !!data.enabled,
          gifts: data.gifts || [],
          sections: data.sections || [],
          gems: data.gems || 0,
        })
      } catch {
        // Never surface this: a failed catalog fetch should leave the chat
        // exactly as it was, not show a broken shop.
      }
    },
    [user],
  )

  useEffect(() => {
    refresh()
  }, [refresh])

  return { ...state, refresh }
}

/**
 * One-shot celebration burst after a gift is sent.
 *
 * Mounted only while celebrating and unmounted by its own timer, so there is no
 * persistent canvas or resize listener sitting in the chat for the 99.9% of the
 * session when nobody is gifting. `recycle={false}` makes it a single burst that
 * falls out of frame rather than a continuous stream.
 */
export function GiftConfetti({ emoji, onDone }) {
  const [size, setSize] = useState({ w: 0, h: 0 })

  // onDone is typically an inline arrow, so a fresh identity every parent
  // render. Depending on it directly would clear and restart the timer on every
  // unrelated chat re-render, leaving the canvas up far longer than intended.
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone

  useEffect(() => {
    const measure = () =>
      setSize({ w: window.innerWidth, h: window.innerHeight })
    measure()
    window.addEventListener('resize', measure)
    const t = setTimeout(() => onDoneRef.current?.(), CONFETTI_MS)
    return () => {
      window.removeEventListener('resize', measure)
      clearTimeout(t)
    }
  }, [])

  // Draw the gift's own emoji as each particle instead of the default coloured
  // rectangles — a shower of roses reads as "you gave her roses", generic
  // confetti reads as "something happened".
  //
  // react-confetti transforms the context per particle as:
  //   translate(x, y) -> rotate(angle) -> scale(1, rotateY)
  // That last one oscillates to fake a card flipping edge-on. It works for a
  // rectangle but mangles a glyph: the emoji ends up squashed and sheared, and
  // at the extremes almost invisible. So the transform is flattened back to a
  // plain translation, keeping only the particle's POSITION and drawing the
  // glyph upright at full size. Safe to do here because the library wraps each
  // particle in save()/restore() and applies no devicePixelRatio scaling, so
  // nothing outside this call depends on the matrix we are discarding.
  const drawEmoji = useCallback(
    (ctx) => {
      if (!emoji) return
      ctx.save()
      if (typeof ctx.getTransform === 'function') {
        const t = ctx.getTransform()
        // t.e / t.f are the translation components — the particle's position.
        ctx.setTransform(1, 0, 0, 1, t.e, t.f)
      }
      ctx.font =
        '26px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(emoji, 0, 0)
      ctx.restore()
    },
    [emoji],
  )

  if (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  ) {
    return null
  }

  if (!size.w) return null

  return (
    <div className="pointer-events-none fixed inset-0 z-[120]">
      <Confetti
        width={size.w}
        height={size.h}
        numberOfPieces={emoji ? 90 : 220}
        recycle={false}
        gravity={0.28}
        initialVelocityY={12}
        tweenDuration={CONFETTI_MS}
        drawShape={emoji ? drawEmoji : undefined}
        // Emoji glyphs carry their own colour, so the palette only applies to
        // the fallback rectangles.
        colors={['#f6c453', '#e0a32e', '#c8871a', '#ffe9a8', '#ffffff']}
      />
    </div>
  )
}

// Flavour accents, used on the SECTION HEADERS only.
//
// The tiles deliberately stay on the tier ladder below. Flavour is already
// carried by the grouping, the label and the blurb, whereas price ranking has
// nowhere else to live — and the chat bubble a gift produces is tinted by TIER
// in chatStyles.css, so re-tinting tiles by flavour would break the
// tile-matches-its-bubble property that ladder exists for.
const FLAVOR_ACCENT = {
  sweet: 'text-emerald-300/80',
  romantic: 'text-indigo-300/80',
  spicy: 'text-rose-300/80',
}

// Tier accents. Same ladder as the chat bubble (chatStyles.css): grey = small,
// indigo (the app's primary) = mid, gold = grand — so a tile and the bubble it
// produces are obviously the same thing.
const TIER_STYLES = {
  small: 'border-white/10 bg-[var(--foreground-bg-color)]',
  mid: 'border-indigo-400/40 bg-indigo-500/10',
  grand: 'border-amber-400/50 bg-amber-400/10',
}
const TIER_PRICE = {
  small: 'opacity-60',
  mid: 'text-indigo-200/80',
  grand: 'text-amber-200/90',
}

export default function GiftShop({
  open,
  onClose,
  user,
  characterId,
  characterName,
  gifts,
  sections,
  gems,
  onGifted,
  owned,
  onNeedGems,
}) {
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState(null)
  // The purchase being attempted, with its idempotency key: kept across
  // retries of the same gift, renewed for another gift or after a success.
  const purchaseRef = useRef(null)

  useEffect(() => {
    if (open) setError(null)
  }, [open])

  const buy = async (gift) => {
    if (busy) return
    setBusy(gift.id)
    setError(null)
    const intent = purchaseIntent(purchaseRef.current, characterId, gift.id)
    purchaseRef.current = intent
    try {
      const idToken = await getIdToken(user)
      const res = await fetch(`${API}/api/chat/gift`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: idToken },
        body: JSON.stringify({
          gift_id: gift.id,
          character_id: characterId,
          // Idempotency key: a double tap or a retry on a flaky connection must
          // not charge twice. The server derives the gift doc id from this, so
          // it is made once per purchase, not per attempt (src/lib/gift-purchase.js).
          key: intent.key,
        }),
      })
      const data = await res.json().catch(() => ({}))

      if (res.status === 402) {
        onClose?.()
        onNeedGems?.(gift)
        return
      }
      if (res.status === 429) {
        setError("You've reached today's gift limit.")
        return
      }
      if (!res.ok) {
        setError(data.error || 'Something went wrong.')
        return
      }

      // Done: the next gift, even the same one, is a new purchase.
      purchaseRef.current = null
      onClose?.()
      onGifted?.(gift, data.gems_remaining)
    } catch {
      setError('Something went wrong.')
    } finally {
      setBusy(null)
    }
  }

  const content = (
    <div className="space-y-3">
      {/* No gem balance here on purpose — the header already shows it, and
          putting it beside the prices turns choosing a gift into a budgeting
          exercise. `gems` is still used below to disable the unaffordable.

          Explains what a gift is FOR without exposing the mechanic. Affinity is
          hidden by design, so this deliberately describes the felt effect ("how
          she treats you") rather than anything that reads as a meter to fill —
          the moment it sounds like levelling up, it becomes a grind. */}
      <div className="space-y-1 text-sm leading-relaxed text-gray-400">
        <p>
          {characterName
            ? `${characterName} reacts the moment you send it — and she keeps it.`
            : 'She reacts the moment you send it — and she keeps it.'}
        </p>
        <p>
          Gifts stay with her long after the conversation moves on, colouring
          how warm she is with you. They&apos;re also the surest way to make it
          up to her after things have cooled.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/50 bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* What she already has. Shows only real gifts — never greyed-out slots
          for ungifted items, which would turn a keepsake into a set to
          complete and start pressuring spend. */}
      {Array.isArray(owned) && owned.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2.5">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {characterName ? `${characterName} has` : 'Already given'}
          </div>
          <div className="flex flex-wrap gap-2">
            {owned.map((g, i) => (
              <span
                key={`${g.id}-${i}`}
                title={g.name}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-[var(--foreground-bg-color)] px-2.5 py-1 text-xs text-gray-200"
              >
                <span className="text-base leading-none">{g.emoji}</span>
                <span className="max-w-[10rem] truncate">{g.name}</span>
                {g.count > 1 && (
                  <span className="font-semibold text-gray-400">
                    x{g.count}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {(sections?.length
        ? sections
        : [{ flavor: 'all', label: '', blurb: '', gifts: gifts || [] }]
      ).map((section) => (
        <div key={section.flavor || section.tier || 'all'}>
          {section.label && (
            <div className="mb-1.5">
              <div
                className={`text-xs font-semibold uppercase tracking-wide ${
                  FLAVOR_ACCENT[section.flavor] || 'text-gray-400'
                }`}
              >
                {section.label}
              </div>
              {/* The only place each shelf is explained. Comes from the server
                  so the catalog stays one source of truth. */}
              {section.blurb && (
                <div className="mt-0.5 text-[11px] font-normal leading-snug text-gray-500">
                  {section.blurb}
                </div>
              )}
            </div>
          )}
          {/* px-0.5 py-1: the hover scale needs somewhere to grow. Without it
              the outermost tiles in each row are clipped by the modal's
              overflow, which reads as the button overflowing its container. */}
          <div className="grid grid-cols-3 gap-2 px-0.5 py-1 sm:grid-cols-4">
            {section.gifts.map((gift) => {
              const affordable = gems >= gift.gems
              return (
                <button
                  key={gift.id}
                  type="button"
                  title={`${gift.name} — ${gift.gems} gems`}
                  onClick={() => {
                    if (!affordable) {
                      onClose?.()
                      onNeedGems?.(gift)
                      return
                    }
                    buy(gift)
                  }}
                  disabled={!!busy}
                  className={`
                    group flex flex-col items-center justify-center gap-1
                    rounded-lg border
                    px-1.5 py-2.5 text-center text-[11px] font-semibold leading-tight text-white
                    shadow-sm transition-all duration-200
                    hover:scale-[1.02] hover:brightness-110
                    disabled:cursor-not-allowed disabled:hover:scale-100
                    ${TIER_STYLES[gift.tier] || TIER_STYLES.small}
                    ${affordable ? '' : 'opacity-50'}
                  `}
                >
                  <span className="text-xl leading-none transition-transform duration-200 group-hover:scale-110">
                    {gift.emoji}
                  </span>
                  <span className="line-clamp-2 leading-snug">{gift.name}</span>
                  <span
                    className={`flex items-center gap-1 text-[10px] font-normal ${
                      TIER_PRICE[gift.tier] || TIER_PRICE.small
                    }`}
                  >
                    {busy === gift.id ? (
                      'Sending…'
                    ) : (
                      <>
                        <FaGem size={9} />
                        {gift.gems}
                      </>
                    )}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <Modal
      isOpen={open}
      closeModal={onClose}
      title={`Send ${characterName || 'her'} a gift`}
      content={content}
    />
  )
}
